import { useCallback } from 'react';

import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';

export type DestructiveOptions = {
  /** Success toast title shown after the action resolves. */
  success: string;
  /** Fallback error toast title when the failure is not an ApiError with a message. */
  errorFallback: string;
  /** ApiError code meaning "blocked — keep the modal open". When the failure matches,
   *  `run` resolves `{ blocked: true }` instead of toasting (the modal shows its own
   *  blocked copy). */
  blockedCode?: string;
};

/**
 * Runs a destructive mutation (delete / status toggle) with standardized feedback:
 * success → success toast; an ApiError matching `blockedCode` → resolves `{ blocked: true }`
 * (no toast); any other failure → error toast (prefers the ApiError message). Never rethrows.
 *
 * Dialog-agnostic — feed `run` to either `useConfirm().confirm({ onConfirm })` (return
 * ignored) or `<DeleteConfirmModal onConfirm>` (consumes the `{ blocked: true }`).
 */
export function useDestructiveAction(): {
  run: (fn: () => Promise<unknown>, opts: DestructiveOptions) => Promise<{ blocked: true } | void>;
} {
  const toast = useToast();

  const run = useCallback(
    async (
      fn: () => Promise<unknown>,
      opts: DestructiveOptions,
    ): Promise<{ blocked: true } | void> => {
      try {
        await fn();
        toast.success({ title: opts.success });
      } catch (error) {
        if (opts.blockedCode && error instanceof ApiError && error.code === opts.blockedCode) {
          return { blocked: true };
        }
        toast.error({ title: error instanceof ApiError ? error.message : opts.errorFallback });
      }
    },
    [toast],
  );

  return { run };
}
